import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { ArrowLeft, Check, RotateCcw, Loader2 } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { getReportColumns, updateReportColumns, type ReportColumnsRequest } from '../../../../../services/reportColumnsApi';
import { useProductionModalAudit } from '../../../../audit/useProductionModalAudit';
import { logReportColumnConfigUpdated } from '../../../utils/productionAudit';

const EMPTY_SELECTED_COLUMNS: string[] = [];

interface ReportColumn {
  id: string;
  label: string;
  enabled: boolean;
  unit?: string;
  unitOptions?: string[];
}

interface UnitConfig {
  id: string;
  label: string;
  unit: string;
  unitOptions: string[];
}

interface ReportColumnConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedColumns: string[]) => void;
  onUnitsSaved?: (units: Record<string, string>) => void;
  initialSelectedColumns?: string[];
  projectId?: number;
  projectName?: string | null;
  /** 'report' = full modal (Report Generation). 'status' = units only (Status Configuration). */
  mode?: 'report' | 'status';
  /** Status Configuration checkbox state — only checked parameters can change units in status mode. */
  statusEnabledParameters?: Record<string, boolean>;
}

export const ReportColumnConfigModal: React.FC<ReportColumnConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onUnitsSaved,
  initialSelectedColumns = EMPTY_SELECTED_COLUMNS,
  projectId,
  projectName,
  mode = 'report',
  statusEnabledParameters = {}
}) => {
  const modalName = mode === 'status' ? 'Report Column Units' : 'Report Column Configuration';
  useProductionModalAudit(isOpen, modalName, projectId, projectName);
  const isStatusMode = mode === 'status';
  // Define unit options for different parameter types
  const unitOptions = {
    current: ['A', 'mA'],
    voltage: ['V', 'mV'],
    speed: ['in/min', 'cm/min', 'mm/min'],
    energy: ['kJ/in', 'kJ/cm', 'kJ/mm'],
    length: ['in', 'cm', 'mm'],
    frequency: ['cyc/min', 'Hz', 'kHz'],
    impedance: ['mOhm'],
    diameter: ['in', 'cm', 'mm'],
    thickness: ['in', 'cm', 'mm'],
    distance: ['in', 'cm', 'mm'],
    target: ['A'],
  };

  // Define standalone units that don't have corresponding columns
  const defaultStandaloneUnits = [
    { id: 'pipe_diameter', label: 'Pipe Diameter (in)', unit: 'in', unitOptions: unitOptions.diameter },
    { id: 'wall_thickness', label: 'Wall Thickness', unit: 'in', unitOptions: unitOptions.thickness },
    { id: 'band_diameter', label: 'Band Diameter', unit: 'in', unitOptions: unitOptions.diameter },
    { id: 'wire_diameter', label: 'Wire Diameter', unit: 'in', unitOptions: unitOptions.diameter },
    { id: 'logging_distance', label: 'Logging Distance', unit: 'in', unitOptions: unitOptions.distance },
    { id: 'distance', label: 'Distance', unit: 'in', unitOptions: unitOptions.distance }
  ];

  // Define report sections with unit options
  const summaryColumns: ReportColumn[] = [
    { id: 'summary_page', label: 'Summary Page', enabled: false }
  ];

  const dataColumns: ReportColumn[] = [
    { id: 'current', label: 'Arc Current', enabled: false, unit: 'A', unitOptions: unitOptions.current },
    { id: 'volts', label: 'Arc Volts', enabled: false, unit: 'V', unitOptions: unitOptions.voltage },
    { id: 'frequency', label: 'Frequency', enabled: false, unit: 'cyc/min', unitOptions: unitOptions.frequency },
    { id: 'target', label: 'Target', enabled: false, unit: 'A', unitOptions: unitOptions.target },
    { id: 'horizontal_bias', label: 'Horizontal Bias', enabled: false, unit: 'A', unitOptions: unitOptions.current },
    { id: 'arc_impedance', label: 'Arc Impedance', enabled: false, unit: 'mOhm', unitOptions: unitOptions.impedance },
    { id: 'travel_speed', label: 'Travel Speed', enabled: false, unit: 'in/min', unitOptions: unitOptions.speed },
    { id: 'wire_speed', label: 'Wire Speed', enabled: false, unit: 'in/min', unitOptions: unitOptions.speed },
    { id: 'heat', label: 'Heat Input', enabled: false, unit: 'kJ/in', unitOptions: unitOptions.energy },
    { id: 'true_energy', label: 'True Energy', enabled: false, unit: 'kJ/in', unitOptions: unitOptions.energy },
    { id: 'oscillation_width', label: 'Oscillation Width', enabled: false, unit: 'in', unitOptions: unitOptions.length },
    { id: 'total_wire_consumed', label: 'Total Wire Consumed', enabled: false, unit: 'in', unitOptions: unitOptions.length }
  ];

  const allColumns = [...summaryColumns, ...dataColumns];

  const [columns, setColumns] = useState<ReportColumn[]>(allColumns);
  const [standaloneUnits, setStandaloneUnits] = useState<UnitConfig[]>(defaultStandaloneUnits);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [reportOption, setReportOption] = useState<'avg' | 'low-avg-high'>('avg');
  const [initialColumns, setInitialColumns] = useState<ReportColumn[]>(allColumns);
  const [initialStandaloneUnits, setInitialStandaloneUnits] = useState<UnitConfig[]>(defaultStandaloneUnits);
  const [initialReportOption, setInitialReportOption] = useState<'avg' | 'low-avg-high'>('avg');
  const loadAttemptRef = useRef<string | null>(null);

  // Load report columns from API when modal opens (once per open)
  useEffect(() => {
    if (!isOpen) {
      loadAttemptRef.current = null;
      return;
    }

    const loadKey = `${mode}-${projectId ?? 'local'}-${initialSelectedColumns.join(',')}`;
    if (loadAttemptRef.current === loadKey) return;
    loadAttemptRef.current = loadKey;

    if (projectId) {
      loadReportColumns();
    } else if (initialSelectedColumns.length > 0) {
      // Fallback to initial selected columns if no projectId
      const updatedColumns = allColumns.map(col => ({
        ...col,
        enabled: initialSelectedColumns.includes(col.id)
      }));
      setColumns(updatedColumns);
      setStandaloneUnits(defaultStandaloneUnits);
      setSelectAll(updatedColumns.every(col => col.enabled));
      // Store initial configuration for reset functionality
      setInitialColumns(updatedColumns);
      setInitialStandaloneUnits(defaultStandaloneUnits);
      setInitialReportOption('avg');
    } else if (isOpen) {
      // Set default selected columns (matching the reference image)
      const defaultSelected = ['current', 'volts', 'travel_speed', 'wire_speed', 'heat'];
      const updatedColumns = allColumns.map(col => ({
        ...col,
        enabled: defaultSelected.includes(col.id)
      }));
      setColumns(updatedColumns);
      setStandaloneUnits(defaultStandaloneUnits);
      setSelectAll(false);
      // Store initial configuration for reset functionality
      setInitialColumns(updatedColumns);
      setInitialStandaloneUnits(defaultStandaloneUnits);
      setInitialReportOption('avg');
    }
  }, [isOpen, projectId, initialSelectedColumns, mode]);

  const buildReportColumnsPayload = (
    cols: ReportColumn[],
    option: 'avg' | 'low-avg-high'
  ): ReportColumnsRequest['report_columns'] => ({
    current: cols.find(col => col.id === 'current')?.enabled || false,
    volts: cols.find(col => col.id === 'volts')?.enabled || false,
    travel_speed: cols.find(col => col.id === 'travel_speed')?.enabled || false,
    true_energy: cols.find(col => col.id === 'true_energy')?.enabled || false,
    heat: cols.find(col => col.id === 'heat')?.enabled || false,
    wire_speed: cols.find(col => col.id === 'wire_speed')?.enabled || false,
    oscillation_width: cols.find(col => col.id === 'oscillation_width')?.enabled || false,
    frequency: cols.find(col => col.id === 'frequency')?.enabled || false,
    target: cols.find(col => col.id === 'target')?.enabled || false,
    horizontal_bias: cols.find(col => col.id === 'horizontal_bias')?.enabled || false,
    total_wire_consumed: cols.find(col => col.id === 'total_wire_consumed')?.enabled || false,
    arc_impedance: cols.find(col => col.id === 'arc_impedance')?.enabled || false,
    summary_page: cols.find(col => col.id === 'summary_page')?.enabled || false,
    is_high_low_avg_required: option === 'low-avg-high',
  });

  const buildUnitsPayload = (
    cols: ReportColumn[],
    standalone: UnitConfig[]
  ): ReportColumnsRequest['units'] => ({
    current: cols.find(col => col.id === 'current')?.unit || 'A',
    volts: cols.find(col => col.id === 'volts')?.unit || 'V',
    travel_speed: cols.find(col => col.id === 'travel_speed')?.unit || 'in/min',
    true_energy: cols.find(col => col.id === 'true_energy')?.unit || 'kJ/in',
    heat: cols.find(col => col.id === 'heat')?.unit || 'kJ/in',
    wire_speed: cols.find(col => col.id === 'wire_speed')?.unit || 'in/min',
    oscillation_width: cols.find(col => col.id === 'oscillation_width')?.unit || 'in',
    frequency: cols.find(col => col.id === 'frequency')?.unit || 'cyc/min',
    target: cols.find(col => col.id === 'target')?.unit || 'A',
    horizontal_bias: cols.find(col => col.id === 'horizontal_bias')?.unit || 'A',
    total_wire_consumed: cols.find(col => col.id === 'total_wire_consumed')?.unit || 'in',
    arc_impedance: cols.find(col => col.id === 'arc_impedance')?.unit || 'mOhm',
    band_diameter: standalone.find(unit => unit.id === 'band_diameter')?.unit || 'in',
    wire_diameter: standalone.find(unit => unit.id === 'wire_diameter')?.unit || 'in',
    logging_distance: standalone.find(unit => unit.id === 'logging_distance')?.unit || 'in',
    distance: standalone.find(unit => unit.id === 'distance')?.unit || 'in',
    pipe_diameter: standalone.find(unit => unit.id === 'pipe_diameter')?.unit || 'in',
    wall_thickness: standalone.find(unit => unit.id === 'wall_thickness')?.unit || 'in',
  });

  const loadReportColumns = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    try {
      const response = await getReportColumns(projectId);
      const reportColumns = response.data.project.report_columns;
      const units = (response.data.project as any).units || {};
      
      // Map API response to our column structure with both enabled state and units
      const updatedColumns = allColumns.map(col => ({
        ...col,
        enabled: reportColumns[col.id as keyof typeof reportColumns] || false,
        unit: units[col.id as keyof typeof units] || col.unit
      }));
      
      // Map API response to our standalone units structure
      const updatedStandaloneUnits = defaultStandaloneUnits.map(unit => ({
        ...unit,
        unit: units[unit.id as keyof typeof units] || unit.unit
      }));
      
      setColumns(updatedColumns);
      setStandaloneUnits(updatedStandaloneUnits);
      setSelectAll(updatedColumns.every(col => col.enabled));
      
      // Set report option based on is_high_low_avg_required
      const reportOptionValue =  'avg' //reportColumns.is_high_low_avg_required ? 'low-avg-high' : 'avg';
      setReportOption(reportOptionValue);
      
      // Store initial configuration for reset functionality
      setInitialColumns(updatedColumns);
      setInitialStandaloneUnits(updatedStandaloneUnits);
      setInitialReportOption(reportOptionValue);
    } catch (error) {
      // Fallback to default columns on error
      const defaultSelected = ['current', 'volts', 'travel_speed', 'wire_speed', 'heat'];
      const updatedColumns = allColumns.map(col => ({
        ...col,
        enabled: defaultSelected.includes(col.id)
      }));
      setColumns(updatedColumns);
      setStandaloneUnits(defaultStandaloneUnits);
      setSelectAll(false);
      setReportOption('avg');
      // Store initial configuration for reset functionality
      setInitialColumns(updatedColumns);
      setInitialStandaloneUnits(defaultStandaloneUnits);
      setInitialReportOption('avg');
    } finally {
      setIsLoading(false);
    }
  };

  const handleColumnToggle = (columnId: string) => {
    const updatedColumns = columns.map(col =>
      col.id === columnId ? { ...col, enabled: !col.enabled } : col
    );
    setColumns(updatedColumns);
    setSelectAll(updatedColumns.every(col => col.enabled));
  };

  const handleColumnUnitChange = (columnId: string, newUnit: string) => {
    const updatedColumns = columns.map(col =>
      col.id === columnId ? { ...col, unit: newUnit } : col
    );
    setColumns(updatedColumns);
  };

  const handleStandaloneUnitChange = (unitId: string, newUnit: string) => {
    const updatedStandaloneUnits = standaloneUnits.map(unit =>
      unit.id === unitId ? { ...unit, unit: newUnit } : unit
    );
    setStandaloneUnits(updatedStandaloneUnits);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setColumns(columns.map(col => ({ ...col, enabled: newSelectAll })));
  };

  const handleResetToDefault = () => {
    // Reset to initial configuration fetched from API
    setColumns(initialColumns);
    setStandaloneUnits(initialStandaloneUnits);
    setSelectAll(initialColumns.every(col => col.enabled));
    setReportOption(initialReportOption);
  };

  const handleSave = async () => {
    if (!projectId) {
      console.warn('No projectId provided - saving locally only');
      // Fallback to original behavior if no projectId
      const selectedColumns = columns.filter(col => col.enabled).map(col => col.id);
      onSave(selectedColumns);
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      const reportColumnsRequest: ReportColumnsRequest = {
        report_columns: isStatusMode
          ? buildReportColumnsPayload(initialColumns, initialReportOption)
          : buildReportColumnsPayload(columns, reportOption),
        units: isStatusMode
          ? buildUnitsPayload(columns, initialStandaloneUnits)
          : buildUnitsPayload(columns, standaloneUnits),
      };

      console.log('Updating report columns for projectId:', projectId);
      console.log('Request payload:', reportColumnsRequest);

      // Update the report columns via API
      const response = await updateReportColumns(projectId, reportColumnsRequest);
      console.log('Report columns updated successfully:', response);

      const selectedColumns = columns.filter(col => col.enabled).map(col => col.id);
      logReportColumnConfigUpdated({
        projectId,
        projectName,
        mode,
        enabledColumnCount: selectedColumns.length,
        enabledColumns: selectedColumns,
        units: reportColumnsRequest.units,
      });
      
      onUnitsSaved?.(reportColumnsRequest.units);
      onSave(selectedColumns);
      onClose();
    } catch (error) {
      console.error('Error updating report columns:', error);
      // Still save locally even if API fails
      const selectedColumns = columns.filter(col => col.enabled).map(col => col.id);
      onSave(selectedColumns);
      onClose();
    } finally {
      setIsSaving(false);
    }
    
  };

  const selectedCount = columns.filter(col => col.enabled).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          "w-[95vw] max-w-[1100px] min-w-[320px] max-h-[92vh] p-0 gap-0 border bg-white shadow-2xl overflow-hidden flex flex-col",
          "sm:w-[90vw] sm:max-w-[600px]",
          "md:w-[85vw] md:max-w-[800px]",
          "lg:w-[80vw] lg:max-w-[1000px]",
          "xl:w-[75vw] xl:max-w-[1100px]"
        )}
      >
          <div className="flex flex-col min-h-0 max-h-[92vh]">
            <DialogHeader className="px-4 sm:px-6 py-3 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
              <div className="flex items-center gap-4">
                <Button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  variant="outline"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-500" />
                </Button>
                <div>
                  <DialogTitle className="text-base font-bold text-gray-900">
                    {isStatusMode ? 'Configure Units' : 'Configure Report Columns'}
                  </DialogTitle>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {isStatusMode
                      ? 'Update display units for status configuration parameters'
                      : 'Select the required parameters to generate the report'}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 min-h-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                    <span className="text-gray-600 text-lg">Loading report configuration...</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                {!isStatusMode && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="selectAll"
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <Label htmlFor="selectAll" className="text-sm font-medium text-gray-700 cursor-pointer">
                      Select all ({selectedCount})
                    </Label>
                  </div>

                  <Button
                    onClick={handleResetToDefault}
                    variant="outline"
                    className="flex items-center gap-2 h-8 px-3 text-xs"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Reset Configuration To Default
                  </Button>
                </div>
                )}

                {isStatusMode && (
                  <div className="flex justify-end">
                    <Button
                      onClick={handleResetToDefault}
                      variant="outline"
                      className="flex items-center gap-2 h-8 px-3 text-xs"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset Units To Default
                    </Button>
                  </div>
                )}


                {/* Data Columns Section */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    {isStatusMode ? 'Parameter Units' : 'Data Columns'}
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                    {dataColumns.map((column) => {
                      const columnState = columns.find(col => col.id === column.id);
                      const isUnitEditable = isStatusMode
                        ? Boolean(statusEnabledParameters[column.id])
                        : Boolean(columnState?.enabled);
                      return (
                        <div
                          key={column.id}
                          className="grid grid-cols-[auto_1fr_6rem] items-center gap-2 px-2 py-1.5 border rounded-lg bg-gray-50"
                        >
                          <Checkbox
                            id={column.id}
                            checked={isStatusMode
                              ? Boolean(statusEnabledParameters[column.id])
                              : Boolean(columnState?.enabled)}
                            onCheckedChange={() => handleColumnToggle(column.id)}
                            disabled={isStatusMode}
                            className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded disabled:opacity-60"
                          />
                          <Label
                            htmlFor={column.id}
                            className={cn(
                              'text-xs font-medium text-gray-700 min-w-0 truncate',
                              isStatusMode ? 'cursor-default' : 'cursor-pointer'
                            )}
                            title={column.label}
                          >
                            {column.label}
                          </Label>
                          {column.unitOptions ? (
                            <Select
                              value={columnState?.unit || column.unit}
                              onValueChange={(value) => handleColumnUnitChange(column.id, value)}
                              disabled={!isUnitEditable}
                            >
                              <SelectTrigger className="h-7 px-2 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {column.unitOptions.map((unit) => (
                                  <SelectItem key={unit} value={unit} className="text-xs">
                                    {unit}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {!isStatusMode && (
                <>
                {/* Standalone Units Configuration Section */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Additional Units Configuration</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                    {standaloneUnits.map((unit) => (
                      <div
                        key={unit.id}
                        className="grid grid-cols-[1fr_6rem] items-center gap-2 px-2 py-1.5 border rounded-lg bg-gray-50"
                      >
                        <Label
                          className="text-xs font-medium text-gray-700 min-w-0 truncate"
                          title={unit.label}
                        >
                          {unit.label}
                        </Label>
                        <Select
                          value={unit.unit}
                          onValueChange={(value) => handleStandaloneUnitChange(unit.id, value)}
                        >
                          <SelectTrigger className="h-7 px-2 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {unit.unitOptions.map((unitOption) => (
                              <SelectItem key={unitOption} value={unitOption} className="text-xs">
                                {unitOption}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Report Options — single compact bar */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Report Options</Label>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-3 py-2 border rounded-lg bg-gray-50">
                    <span className="text-xs font-medium text-gray-600">Statistical Summary:</span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reportOption"
                        value="low-avg-high"
                        checked={reportOption === 'low-avg-high'}
                        onChange={(e) => setReportOption(e.target.value as 'avg' | 'low-avg-high')}
                        className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                      />
                      <span className="text-xs text-gray-700">Low/Avg/High</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="reportOption"
                        value="avg"
                        checked={reportOption === 'avg'}
                        onChange={(e) => setReportOption(e.target.value as 'avg' | 'low-avg-high')}
                        className="h-4 w-4 text-red-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-red-600"
                      />
                      <span className="text-xs text-gray-700">Avg</span>
                    </label>
                    <div className="h-4 w-px bg-gray-300 mx-1" />
                    <label htmlFor="summary_page" className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        id="summary_page"
                        checked={columns.find(col => col.id === 'summary_page')?.enabled || false}
                        onCheckedChange={() => handleColumnToggle('summary_page')}
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <span className="text-xs font-medium text-gray-700">Include Summary Page</span>
                    </label>
                  </div>
                </div>
                </>
                )}
                </div>
              )}
            </div>

            {/* Action Buttons - Fixed at bottom */}
            <div className="flex flex-row justify-end gap-3 px-4 sm:px-5 py-3 border-t bg-white flex-shrink-0">
              <Button
                onClick={onClose}
                variant="outline"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-red-600 hover:bg-red-700 text-white px-6"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </Button>
            </div>
          </div>
      </DialogContent>
    </Dialog>
  );
};
